import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import quizQuestions from "../../data/quizQuestions";
import {
  calculateTraitScores,
  matchCareers,
  getPersonalityType,
} from "../../utils/scoring";

const CSS = `
  .pf-quiz { min-height: 100vh; padding: 100px 0 80px; position: relative; overflow: hidden; }
  .pf-quiz-bg {
    position: absolute; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 45% 40% at 15% 50%, rgba(255,107,53,0.04) 0%, transparent 55%),
      radial-gradient(ellipse 40% 40% at 85% 30%, rgba(0,212,170,0.03) 0%, transparent 50%);
  }
  .pf-quiz-wrap {
    max-width: 700px; margin: 0 auto; padding: 0 24px;
    position: relative; z-index: 1;
  }

  /* header */
  .pf-quiz-header { text-align: center; margin-bottom: 40px; }
  .pf-quiz-count {
    font-family: 'JetBrains Mono', monospace; font-size: 0.78em; font-weight: 600;
    color: rgba(255,107,53,0.6); margin-bottom: 8px;
  }
  .pf-quiz-emoji { font-size: 2.8em; margin-bottom: 14px; display: block; animation: float 3s ease-in-out infinite; }
  .pf-quiz-question {
    font-family: 'DM Serif Display', serif; font-size: clamp(1.4em, 3vw, 1.9em);
    color: #F0F0F5; line-height: 1.35; margin-bottom: 6px;
    animation: fadeInUp 0.5s ease both;
  }

  /* progress */
  .pf-quiz-progress-wrap {
    position: fixed; top: 0; left: 0; right: 0; height: 4px;
    background: rgba(255,255,255,0.04); z-index: 9999;
  }
  .pf-quiz-progress {
    height: 100%; border-radius: 0 4px 4px 0;
    background: linear-gradient(90deg, #FF6B35, #FFD23F, #00D4AA);
    background-size: 200% 100%;
    animation: gradientShift 3s ease infinite;
    transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  }

  /* options */
  .pf-quiz-options {
    display: flex; flex-direction: column; gap: 12px;
    animation: fadeInUp 0.5s ease 0.1s both;
  }
  .pf-quiz-option {
    position: relative; padding: 18px 22px; border-radius: 16px;
    background: rgba(255,255,255,0.025); border: 1.5px solid rgba(255,255,255,0.06);
    cursor: pointer; transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
    display: flex; align-items: center; gap: 14px;
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.92em;
    color: rgba(240,240,245,0.65); font-weight: 500;
  }
  .pf-quiz-option:hover {
    background: rgba(255,107,53,0.06);
    border-color: rgba(255,107,53,0.2);
    transform: translateX(6px);
    color: #F0F0F5;
  }
  .pf-quiz-option.selected {
    background: rgba(255,107,53,0.1);
    border-color: rgba(255,107,53,0.4);
    color: #FF6B35;
    box-shadow: 0 4px 20px rgba(255,107,53,0.12);
    transform: translateX(8px);
  }
  .pf-quiz-option-letter {
    width: 32px; height: 32px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 0.82em;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    transition: all 0.3s ease;
  }
  .pf-quiz-option.selected .pf-quiz-option-letter {
    background: linear-gradient(135deg, #FF6B35, #FF8A5C);
    border-color: transparent; color: #fff;
  }
  .pf-quiz-option:hover .pf-quiz-option-letter {
    background: rgba(255,107,53,0.15);
    border-color: rgba(255,107,53,0.3);
  }

  /* nav buttons */
  .pf-quiz-nav {
    display: flex; justify-content: space-between; align-items: center;
    margin-top: 36px; gap: 14px;
  }
  .pf-quiz-back {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 24px; border-radius: 50px;
    background: transparent; border: 1.5px solid rgba(255,255,255,0.06);
    color: rgba(240,240,245,0.4); font-weight: 600; font-size: 0.88em;
    cursor: pointer; transition: all 0.3s ease;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .pf-quiz-back:hover {
    background: rgba(255,255,255,0.03);
    border-color: rgba(255,255,255,0.1);
    color: rgba(240,240,245,0.7);
  }
  .pf-quiz-back:disabled { opacity: 0.3; cursor: not-allowed; }
  .pf-quiz-next {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 13px 28px; border-radius: 50px;
    background: linear-gradient(135deg, #FF6B35, #FF8A5C);
    color: #fff; font-weight: 700; font-size: 0.9em;
    cursor: pointer; border: none;
    box-shadow: 0 6px 24px rgba(255,107,53,0.3);
    transition: all 0.3s ease;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .pf-quiz-next:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 32px rgba(255,107,53,0.45);
  }
  .pf-quiz-next:disabled {
    opacity: 0.4; cursor: not-allowed;
    transform: none; box-shadow: 0 4px 16px rgba(255,107,53,0.15);
  }

  /* question counter pills */
  .pf-quiz-dots {
    display: flex; justify-content: center; gap: 4px; margin-bottom: 24px;
    flex-wrap: wrap;
  }
  .pf-quiz-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: rgba(255,255,255,0.06); transition: all 0.3s ease;
  }
  .pf-quiz-dot.done { background: rgba(0,212,170,0.5); }
  .pf-quiz-dot.current { background: #FF6B35; box-shadow: 0 0 8px rgba(255,107,53,0.5); width: 24px; border-radius: 4px; }

  /* intro screen */
  .pf-quiz-intro { text-align: center; max-width: 560px; margin: 0 auto; }
  .pf-quiz-intro-icon { font-size: 4em; margin-bottom: 20px; display: block; animation: float 4s ease-in-out infinite; }
  .pf-quiz-intro-title {
    font-family: 'DM Serif Display', serif; font-size: clamp(1.8em, 3.5vw, 2.4em);
    color: #F0F0F5; margin-bottom: 14px;
  }
  .pf-quiz-intro-desc {
    font-size: 0.95em; color: rgba(240,240,245,0.45); line-height: 1.8; margin-bottom: 32px;
  }
  .pf-quiz-intro-info {
    display: flex; justify-content: center; gap: 24px; margin-bottom: 36px;
    flex-wrap: wrap;
  }
  .pf-quiz-intro-stat {
    display: flex; align-items: center; gap: 8px;
    font-size: 0.84em; color: rgba(240,240,245,0.4);
  }
  .pf-quiz-intro-stat span:first-child { font-size: 1.2em; }

  @media(max-width: 600px) {
    .pf-quiz-option { padding: 14px 16px; font-size: 0.86em; }
    .pf-quiz-question { font-size: 1.3em; }
    .pf-quiz-nav { flex-direction: column; }
    .pf-quiz-back, .pf-quiz-next { width: 100%; justify-content: center; }
  }
`;

const LETTERS = ["A", "B", "C", "D"];

export default function CareerTest() {
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(
    Array(quizQuestions.length).fill(null),
  );
  const [selectedIndex, setSelectedIndex] = useState(null);
  const navigate = useNavigate();

  const question = quizQuestions[current];
  const progress = ((current + 1) / quizQuestions.length) * 100;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [current, started]);

  const handleSelect = useCallback(
    (option, index) => {
      setSelectedIndex(index);
      const newAnswers = [...answers];
      newAnswers[current] = option;
      setAnswers(newAnswers);
    },
    [answers, current],
  );

  const handleNext = useCallback(() => {
    if (current < quizQuestions.length - 1) {
      setCurrent((c) => c + 1);
      setSelectedIndex(answers[current + 1] ? answers[current + 1]._idx : null);
    } else {
      // Quiz complete — calculate results
      const traits = calculateTraitScores(answers);
      const matched = matchCareers(traits);
      const personality = getPersonalityType(traits);

      // Store results in sessionStorage
      const results = {
        traits,
        careers: matched.slice(0, 5),
        personality,
        allCareers: matched,
        timestamp: Date.now(),
      };
      sessionStorage.setItem("pf-results", JSON.stringify(results));
      navigate("/results");
    }
  }, [current, answers, navigate]);

  const handleBack = useCallback(() => {
    if (current > 0) {
      setCurrent((c) => c - 1);
      const prev = answers[current - 1];
      setSelectedIndex(prev ? prev._idx : null);
    }
  }, [current, answers]);

  // Also store the option index for back navigation
  const handleSelectWithIndex = (option, index) => {
    const optionWithIdx = { ...option, _idx: index };
    setSelectedIndex(index);
    const newAnswers = [...answers];
    newAnswers[current] = optionWithIdx;
    setAnswers(newAnswers);
  };

  if (!started) {
    return (
      <>
        <style>{CSS}</style>
        <section className="pf-quiz">
          <div className="pf-quiz-bg" />
          <div className="pf-quiz-wrap">
            <div className="pf-quiz-intro">
              <span className="pf-quiz-intro-icon">🧭</span>
              <h1 className="pf-quiz-intro-title">
                Career Aptitude <span className="gradient-text">Test</span>
              </h1>
              <p className="pf-quiz-intro-desc">
                Answer 35 fun questions about your personality, interests, and
                thinking style. Our algorithm will analyze your traits across 5
                dimensions and match you with the best career paths.
              </p>
              <div className="pf-quiz-intro-info">
                <div className="pf-quiz-intro-stat">
                  <span>⏱️</span> ~5 minutes
                </div>
                <div className="pf-quiz-intro-stat">
                  <span>❓</span> 35 questions
                </div>
                <div className="pf-quiz-intro-stat">
                  <span>🎯</span> 5 trait dimensions
                </div>
                <div className="pf-quiz-intro-stat">
                  <span>🔒</span> 100% private
                </div>
              </div>
              <button className="btn-primary" onClick={() => setStarted(true)}>
                🚀 Start the Test
              </button>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="pf-quiz-progress-wrap">
        <div className="pf-quiz-progress" style={{ width: `${progress}%` }} />
      </div>
      <section className="pf-quiz">
        <div className="pf-quiz-bg" />
        <div className="pf-quiz-wrap">
          {/* Dots */}
          <div className="pf-quiz-dots">
            {quizQuestions.map((_, i) => (
              <div
                key={i}
                className={`pf-quiz-dot${i < current ? " done" : ""}${i === current ? " current" : ""}`}
              />
            ))}
          </div>

          {/* Header */}
          <div className="pf-quiz-header" key={`q-${current}`}>
            <div className="pf-quiz-count">
              Question {current + 1} of {quizQuestions.length}
            </div>
            <span className="pf-quiz-emoji">{question.emoji}</span>
            <h2 className="pf-quiz-question">{question.question}</h2>
          </div>

          {/* Options */}
          <div className="pf-quiz-options" key={`o-${current}`}>
            {question.options.map((opt, i) => (
              <div
                key={i}
                className={`pf-quiz-option${selectedIndex === i ? " selected" : ""}`}
                onClick={() => handleSelectWithIndex(opt, i)}
              >
                <div className="pf-quiz-option-letter">{LETTERS[i]}</div>
                <span>{opt.text}</span>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="pf-quiz-nav">
            <button
              className="pf-quiz-back"
              onClick={handleBack}
              disabled={current === 0}
            >
              ← Back
            </button>
            <button
              className="pf-quiz-next"
              onClick={handleNext}
              disabled={selectedIndex === null && !answers[current]}
            >
              {current === quizQuestions.length - 1
                ? "See Results 🎉"
                : "Next →"}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
